import React, { FunctionComponent, useCallback, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { Radio } from "@/components/Radio/Radio";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";

import {
	useGetGeoCountriesMutation,
	useGetGeoRegionsQuery,
} from "@/pages/Private/redux/companyEnrich/companyEnrich.api";

import { EnrichmentFilterConfig } from "../../ListEditDetails";
import { CountrySelection } from "../../enum/icp.enum";

interface PersonalLocationColumnProps {
	titleFilters?: EnrichmentFilterConfig;
	setTitleFilters: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
	prio: "first" | "second" | "third";
}

/** Right column of a search profile: where the person should be located (anywhere / HQ / custom). */
export const PersonalLocationColumn: FunctionComponent<PersonalLocationColumnProps> = ({
	titleFilters,
	setTitleFilters,
	prio,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`defineIcps.defineIcp.${key}`);

	// Geo lookup hooks (same as CompanyEnrich)
	const [getGeoCountries] = useGetGeoCountriesMutation();
	const { data: geoRegionsData } = useGetGeoRegionsQuery();
	const countryNameMapRef = useRef<Map<string, string>>(new Map());

	// Preload country names for already-selected codes
	useEffect(() => {
		const codes = titleFilters?.[prio]?.countries;

		if (codes && codes.length > 0) {
			const unknownCodes = codes.filter((c) => !countryNameMapRef.current.has(c));

			if (unknownCodes.length > 0) {
				getGeoCountries({}).then((res) => {
					if ("data" in res && res.data) {
						for (const country of res.data.items) {
							countryNameMapRef.current.set(country.code, country.name);
						}
					}
				});
			}
		}
	}, []);

	const fetchCountries = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await getGeoCountries({ query }).unwrap();

			return result.items.map((item) => ({ id: item.code, title: item.name }));
		},
		[getGeoCountries]
	);

	const regionOptions = (geoRegionsData?.items || []).map((item) => ({
		id: item.name,
		title: item.name,
	}));

	const geoRegions = geoRegionsData?.items || [];

	const handleRegionSelect = useCallback(
		async (value?: { id: string | number; title: string }[]) => {
			const selectedRegionNames = value?.map((r) => r.id.toString()) || [];

			// Collect country codes from selected regions
			const regionCountryCodes = new Set<string>();

			for (const regionName of selectedRegionNames) {
				const region = geoRegions.find((r) => r.name === regionName);

				if (region) {
					region.countries.forEach((code) => regionCountryCodes.add(code));
				}
			}

			const regionCodes = [...regionCountryCodes];

			// Fetch names for any codes we don't have yet
			const unknownCodes = regionCodes.filter((code) => !countryNameMapRef.current.has(code));

			if (unknownCodes.length > 0) {
				const allCountries = await fetchCountries("");

				for (const country of allCountries) {
					countryNameMapRef.current.set(country.id, country.title);
				}
			}

			setTitleFilters((prev) => {
				const currentCountries = prev?.[prio]?.countries || [];
				const mergedCodes = [...new Set([...currentCountries, ...regionCodes])];

				return {
					...(prev || {}),
					[prio]: {
						...(prev?.[prio] || {}),
						regions: selectedRegionNames,
						countries: mergedCodes,
					},
				};
			});
		},
		[geoRegions, fetchCountries, prio, setTitleFilters]
	);

	return (
		<div className="w-full flex flex-col  self-stretch">
			<div className="text-sm text-text mb-3">{ts("personalLocation")}</div>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.countrySelection === undefined}
					name="position"
					value={undefined}
					onChange={() => {
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								countrySelection: undefined,
							},
						});
					}}
				>
					<span className="text-text-meta ml-2">{ts("countrySelection.ANY")}</span>
				</Radio>
			</div>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.countrySelection === CountrySelection.HQ}
					name="position"
					value={CountrySelection.HQ}
					onChange={() =>
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								countrySelection: CountrySelection.HQ,
							},
						})
					}
				>
					<span className="text-text-meta ml-2">{ts("countrySelection.HQ")}</span>
				</Radio>
			</div>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.countrySelection === CountrySelection.CUSTOM}
					name="position"
					value={CountrySelection.CUSTOM}
					onChange={() =>
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								countrySelection: CountrySelection.CUSTOM,
							},
						})
					}
				>
					<span className="text-text-meta ml-2">{ts("countrySelection.CUSTOM")}</span>
				</Radio>
				{titleFilters?.[prio]?.countrySelection === CountrySelection.CUSTOM && (
					<div className="mt-2 ml-8 flex flex-col items-start self-stretch">
						<div className="flex flex-col gap-4 w-full mb-4">
							<MultiAutoComplete
								data={regionOptions}
								handleSelect={handleRegionSelect}
								label={ts("regionsLabel")}
								placeholder={ts("regionsPlaceholder")}
								selectedItems={
									titleFilters?.[prio]?.regions?.map((r) => ({
										id: r,
										title: r,
									})) || []
								}
							/>
						</div>
						<div className="flex flex-col gap-4 w-full mb-4">
							<CompanyEnrichAutoComplete
								fetchFn={fetchCountries}
								handleSelect={(value) => {
									const codes = value?.map((r) => r.id) || [];

									value?.forEach((r) => countryNameMapRef.current.set(r.id, r.title));
									setTitleFilters((prev) => ({
										...(prev || {}),
										[prio]: {
											...(prev?.[prio] || {}),
											countries: codes,
										},
									}));
								}}
								label={ts("countriesLabel")}
								placeholder={ts("countriesPlaceholder")}
								selectedItems={
									titleFilters?.[prio]?.countries?.map((r) => ({
										id: r,
										title: countryNameMapRef.current.get(r) || r,
									})) || []
								}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
